import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AppPropertiesProvider } from "./contexts/app-properties.context";
import { FollowupProvider } from "./contexts/followup.context";
import { AcademicYearProvider } from "./_context/AcademicYearContext";
import { ITeachConfigProvider } from "./_context/PresetContext";
import { EntityLabelsProvider } from "./_context/EntityLabelsContext";
import { AccessControlProvider } from "./access-control/AccessControlContext";
import { initializeSharedComponents } from "./_config/init-shared-components";
import "./_services/axiositeachWrapper"; // side-effect: installs 401/403 suppression on the default axios instance

import "./i18n/i18n"; // Initialize i18next (side-effect import — must be before render)
import AutoTranslate from "./i18n/AutoTranslate";
import "./index.css";
import "./theme/tokens.css";
import "./theme/bootstrap-dark.css";
import { applyDefaultTheme, applyTheme } from "./theme/themeEngine";
import { fetchAppProperties } from "./_services/AppPropertiesService";
import reportWebVitals from "./reportWebVitals";
import Routes from "./routes/index";

// Initialize shared components with iteach configuration
initializeSharedComponents();

// Theme: bundled defaults synchronously (no-op if the index.html boot script
// already restored a cached tenant theme), then the real tenant theme from the
// public S3 app-properties.json — fire-and-forget, defaults stay on failure.
applyDefaultTheme();
fetchAppProperties().then((props) => {
  if (props) applyTheme(props.theme);
});

// Detect ?demo=true/false on any URL and persist to sessionStorage
const _demoParams = new URLSearchParams(window.location.search);
if (_demoParams.get('demo') === 'true') {
  sessionStorage.setItem('demoMode', 'true');
} else if (_demoParams.get('demo') === 'false') {
  sessionStorage.removeItem('demoMode');
}

const container = document.getElementById("root");
const root = createRoot(container!);

// import "~tempusdominus-bootstrap/src/sass/tempusdominus-bootstrap-build.scss";

root.render(
  <BrowserRouter>
    <React.StrictMode>
      <Suspense fallback={<div />}>
        <AutoTranslate>
          <AppPropertiesProvider>
            <ITeachConfigProvider>
              <EntityLabelsProvider>
                <AccessControlProvider>
                  <FollowupProvider>
                    <AcademicYearProvider>
                      <Routes />
                    </AcademicYearProvider>
                  </FollowupProvider>
                </AccessControlProvider>
              </EntityLabelsProvider>
            </ITeachConfigProvider>
          </AppPropertiesProvider>
        </AutoTranslate>
      </Suspense>
    </React.StrictMode>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
